import { asRow } from '../minigames/rows';
import { warningIsInstruction, type RefusalKind } from './refusal-beacon';

export const REFUSAL_SNAPSHOT_SCHEMA = 'janus-refusal-snapshot.v1';
export const REFUSAL_SNAPSHOT_CAP = 4 * 1024 * 1024;
export interface SnapshotEvent {
    id: string; reason: string; kind: RefusalKind; page: string;
    build: string | null; engine: string | null;
    encounter_id: string | null; actor_id: string | null; ability_id: string | null;
}
export interface SnapshotSources { actor?: unknown; encounter?: unknown }
export interface SnapshotCommand { lane: string; command: unknown; outcome: string; reason?: unknown; intent?: unknown }

/** Descriptors avoid application getters/toJSON. Only the three account fields are scrubbed. */
export function snapshotCopy(value: unknown): unknown {
    const ancestors = new WeakSet<object>();
    let visited = 0;
    const copy = (value: unknown, depth: number): unknown => {
        if (++visited > 2_000_000 || depth > 128) throw new Error('snapshot:input-limit');
        if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
        if (typeof value === 'number') return Number.isFinite(value) ? value : null;
        if (typeof value === 'bigint') return String(value);
        if (typeof value !== 'object') return undefined;
        if (ancestors.has(value)) return '[Circular]';
        ancestors.add(value);
        try {
            const out: Record<string, unknown> | unknown[] = Array.isArray(value) ? [] : {};
            for (const [key, descriptor] of Object.entries(Object.getOwnPropertyDescriptors(value))) {
                if (!descriptor.enumerable || !('value' in descriptor) || /^(email|timezone|invite_code)$/i.test(key)) continue;
                const child = copy(descriptor.value, depth + 1);
                if (child !== undefined) Object.defineProperty(out, key, { value: child, enumerable: true, configurable: true, writable: true });
            }
            return out;
        } finally { ancestors.delete(value); }
    };
    try { return copy(value, 0); } catch { return null; }
}

/** Envelopes can be non-enumerable on a painted surface. Never copy its computed cache. */
function heldBases(value: unknown): Record<string, unknown> {
    const host = asRow(value), out: Record<string, unknown> = {};
    for (const key of ['_entity_base', '_inventory_base', '_inventory_held']) {
        const envelope = asRow(host?.[key]);
        if (!envelope) continue;
        const { cache: _computedCache, ...input } = envelope;
        out[key] = input;
    }
    return out;
}

/** Pure selection and copy; transport, scheduling, gzip and the tab budget are injected by callers. */
export function buildRefusalSnapshot(event: SnapshotEvent, sources: SnapshotSources, breadcrumbs: readonly unknown[], commands: readonly SnapshotCommand[]) {
    let header: Record<string, unknown> | undefined;
    try {
        header = { schema: REFUSAL_SNAPSHOT_SCHEMA, ...event, breadcrumbs: breadcrumbs.slice(-8), commands: commands.slice(-5) };
        const actor = heldBases(sources.actor), encounter = heldBases(sources.encounter);
        return snapshotCopy({ ...header, base: { actor, encounter }, truncated: false })
            ?? snapshotCopy({ ...header, base: { actor }, truncated: true })
            ?? snapshotCopy({ ...header, base: {}, truncated: true });
    } catch { return header ? snapshotCopy({ ...header, base: {}, truncated: true }) : null; }
}

/** A reason consumes one of five tab slots, irrespective of the warning/refused duplicate. */
export function createSnapshotBudget(reasons = new Set<string>()) {
    return (event: Pick<SnapshotEvent, 'kind' | 'reason'>, instruction = false): boolean => {
        try {
            if (!['refused', 'skipped', 'page_error', 'warning'].includes(event.kind)
                || event.kind === 'warning' && (instruction || warningIsInstruction(event.reason))
                || reasons.has(event.reason) || reasons.size >= 5) return false;
            reasons.add(event.reason);
            return true;
        } catch { return false; }
    };
}

export async function gzipSnapshot(json: string): Promise<Uint8Array> {
    const stream = new Blob([json]).stream().pipeThrough(new CompressionStream('gzip'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** Full input → actor input only → intent only. Even compressor/hostile-input failures are observer-only. */
export async function packRefusalSnapshot(snapshot: unknown, gzip = gzipSnapshot, cap = REFUSAL_SNAPSHOT_CAP) {
    try {
        const full = asRow(snapshot);
        if (!full) return null;
        let candidate = full;
        for (let step = 0; step < 3; step++) {
            const bytes = await gzip(JSON.stringify(candidate));
            if (bytes.byteLength <= cap) return { bytes, snapshot: candidate };
            const base = asRow(full.base);
            candidate = { ...full, base: step === 0 ? { actor: base?.actor ?? {} } : {}, truncated: true };
        }
    } catch { /* Compression is optional evidence, never an action failure. */ }
    return null;
}
